import type { MachineAttachmentDto, MachineDto } from '@api/machines'
import { StockTypes } from '@common/utils'
import { useTranslation } from 'react-i18next'
import { useGetStockItemsByBrandId } from '@/shared-queries/machines/index'
import { StyledGrid } from '../styled-components/styles'

type SelectMachineStepV2Props = {
  translationPrefix?: string
  inStockChecked: boolean
  brandId?: number
  machineType?: 'attachment' | 'machine'
  onRowClick: (machine: MachineAttachmentDto | MachineDto) => void
  tonnage?: number
  hideStockItems?: boolean
}

export const SelectMachineStepV2 = ({
  translationPrefix,
  inStockChecked,
  brandId,
  machineType,
  onRowClick,
  tonnage,
  hideStockItems
}: SelectMachineStepV2Props) => {
  const { t } = useTranslation('', {
    keyPrefix: translationPrefix ?? 'common'
  })

  const { data: machines = [] } = useGetStockItemsByBrandId({
    brandId: brandId,
    machineType: machineType,
    tonnage
  })

  // biome-ignore lint/suspicious/noExplicitAny: <->
  const handleOptionChanged = (e: any) => {
    const { element } = e
    element?.parentNode?.scrollTo({
      top: 0
    })
  }

  const getDataSource = () => {
    if (hideStockItems) {
      return machines.filter(
        machine => machine.stockType === StockTypes.FACTORY_ORDER
      )
    }

    if (inStockChecked) {
      return machines.filter(
        machine => machine.stockType === StockTypes.IN_STOCK
      )
    }

    return machines
  }

  return (
    <StyledGrid
      key={`machine-grid-${brandId}`}
      columnsProps={[
        {
          caption: t('model'),
          dataField: 'modelName'
        },
        {
          alignment: 'left',
          caption: t('category'),
          dataField: 'category'
        },
        {
          alignment: 'right',
          caption: t('manufactureYear'),
          dataField: 'manufactureYear',
          dataType: 'number'
        },
        {
          alignment: 'left',
          allowFiltering: !inStockChecked,
          allowSorting: !inStockChecked,
          caption: t('stockType'),
          dataField: 'stockType'
        }
      ]}
      headerFilterProps={{
        visible: true
      }}
      onCellPrepared={e =>
        e.rowType === 'data' &&
        e.cellElement.style.setProperty('cursor', 'pointer', 'important')
      }
      searchPanelProps={{
        highlightSearchText: true,
        visible: true
      }}
      selection={{
        mode: 'single'
      }}
      onContentReady={handleOptionChanged}
      dataSource={getDataSource()}
      onSelectionChanged={selectedRows => {
        const { selectedRowsData } = selectedRows

        const selectedRow = selectedRowsData[0]
        onRowClick(selectedRow)
      }}
    />
  )
}
